import type { Ref } from "react";

import { shortcutWords } from "@/lib/shortcuts";

import styles from "./MaximizeButton.module.css";

/** The keys, from the registry the „?" overview lists (Welle 1, idea 16). */
const TOGGLE_KEYS = shortcutWords("maximize");
const EXIT_KEYS = shortcutWords("maximizeExit");

/**
 * The one button that makes a drawing surface fill the window, and puts it
 * back — the same button both ways, so its state is `aria-pressed` and its
 * name never changes („Zeichenfläche maximieren", pressed or not).
 *
 * The tooltip carries the keys, because nothing else on the screen does.
 * Four corners pointing out, or in once pressed: the glyph says which way the
 * next click goes.
 */
export function MaximizeButton({
  pressed,
  onClick,
  ref,
  className,
}: {
  pressed: boolean;
  onClick: () => void;
  ref?: Ref<HTMLButtonElement>;
  className?: string;
}) {
  return (
    <button
      ref={ref}
      type="button"
      className={`${styles.button} ${className ?? ""}`}
      aria-pressed={pressed}
      aria-label="Zeichenfläche maximieren"
      title={pressed ? `Zurück in die Seite (${TOGGLE_KEYS} oder ${EXIT_KEYS})` : `Zeichenfläche maximieren (${TOGGLE_KEYS})`}
      data-role="maximize"
      onClick={onClick}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
        {pressed ? (
          <path d="M5 1v4H1M9 1v4h4M5 13V9H1M9 13V9h4" />
        ) : (
          <path d="M1 5V1h4M13 5V1H9M1 9v4h4M13 9v4H9" />
        )}
      </svg>
      {/* Constant, and contained in the accessible name (WCAG 2.5.3): the
          pressed state is `aria-pressed` and the glyph, not a second word. */}
      <span className={styles.text}>Maximieren</span>
    </button>
  );
}
